/*
RISKS-VALIDATED-HEADER
Header for the risk page
*/

import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import riskStore from "@/features/risks/entities/risk/model/risk.store"
import { useState } from "react"
import { t } from "i18next"
import { formatDate, isToday } from "@/utils/format-date.utils"
import PageHeader from "@/components/page-header"
import RisksSearch from "./RisksSearch"
import RisksDateSelector from "./RisksDateSelector"
import Button from "@/components/button"
import RiskAddModal from "./modals/RiskAddModal"
import { useNavigate } from "react-router-dom"
import unitStore from "../../unit/model/unit.store"
import RisksSort from "./RisksSort"
import { faLayerGroup, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Tooltip from "@/components/tooltip"

type ModalName = "MODAL_ADD_RISK"

interface Props {
  isShared?: boolean
}

export default function RisksValidatedHeader(props: Props) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  //Selectors
  const selectedDate = useAppSelector(riskStore.selectors.selectedDate)
  const units = useAppSelector(unitStore.selectors.list)
  const riskFamilies = useAppSelector(riskStore.selectors.listSelectedByUnits)
  const allRisks = useAppSelector(riskStore.selectors.list)

  //Modal
  const [modal, setModal] = useState<ModalName | null>(null)

  //If only one unit as contributor navigate to page
  function addRisk() {
    if (units.length === 0) return
    if (units.length > 1) {
      setModal("MODAL_ADD_RISK")
    } else {
      dispatch(unitStore.actions.setActive(units[0]))
      navigate("/risks/risks-add", { state: { backTo: "/risks" } })
    }
  }

  function handleCloseRiskAddModal() {
    setModal(null)
    dispatch(unitStore.actions.setActive(null))
    dispatch(
      riskStore.actions.edit({
        key: "UnitId",
        value: null
      })
    )
  }

  function RisksControllers() {
    return (
      <div className="flex">
        <RisksSearch />
        <RisksDateSelector />
        <RisksSort />
      </div>
    )
  }

  if (props.isShared) {
    return <div className="mb-[32px]">{RisksControllers()}</div>
  } else {
    return (
      <PageHeader
        title={
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faTriangleExclamation} className="grey-t" />
            {"Fiches de risques - " + formatDate(selectedDate, false, false)}
          </div>
        }
        subtitle={
          <>
            Consultez le document unique{" "}
            {!isToday(selectedDate) ? " pour la date du " + formatDate(selectedDate, false, false) + "." : "à jour."} (
            <strong>
              <FontAwesomeIcon icon={faLayerGroup} className="mr-1 text-gray-400" />
              {riskFamilies.length}
            </strong>{" "}
            «{" "}
            {riskFamilies.length > 0 && riskFamilies[0].Risks.length > 0
              ? `${Object.keys(riskFamilies[0].Risks[0].attributes || {})[0]}${riskFamilies.length > 1 ? "s" : ""}`
              : "regroupements"}{" "}
            » contenant{" "}
            <strong>
              <FontAwesomeIcon icon={faTriangleExclamation} className="mr-1 text-gray-400" />
              {allRisks.length}
            </strong>{" "}
            fiche{allRisks.length > 1 ? "s" : ""})
          </>
        }
        extraBelowSubtitle={RisksControllers()}>
        {modal === "MODAL_ADD_RISK" && <RiskAddModal onClose={handleCloseRiskAddModal} />}

        {isToday(selectedDate) && (
          <Tooltip text={units.length === 0 ? "Créez d'abord une unité de travail" : ""} position="bottom">
            <div>
              <Button
                onClick={() => addRisk()}
                disabled={units.length === 0}
                className="primary"
                icon={faTriangleExclamation}>
                {t("risks_add")}
              </Button>
            </div>
          </Tooltip>
        )}
      </PageHeader>
    )
  }
}
